import getOrgContracts, {
  ParsedOrgContractType,
} from 'api/SupraController/getOrgContracts';
import React, { createContext, useContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import { debounce } from 'lodash';
import { AuthContext } from 'contexts';
import refreshOwnerContractsByMLSStaffOrgId from 'api/SupraController/refreshOwnerContractsByMLSStaffOrgId';
import { message } from 'antd';
import { TableSortType } from '../MLSStaffPages/types';
import { useSearchParams } from '../AnsweringSearchPage/hooks';

type ContractsConextType = {
  text: string;
  setText: React.Dispatch<React.SetStateAction<string>>;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  total: number;
  setTotal: React.Dispatch<React.SetStateAction<number>>;
  initialTotal: number;
  setInitialTotal: React.Dispatch<React.SetStateAction<number>>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  contracts: ParsedOrgContractType[];
  setContracts: React.Dispatch<React.SetStateAction<ParsedOrgContractType[]>>;
  sort: TableSortType;
  setSort: React.Dispatch<React.SetStateAction<TableSortType>>;
} | null;

const ContractsContext = createContext<ContractsConextType>(null);

export const useContractsContext = () => {
  const contractsContext = useContext(ContractsContext);

  if (!contractsContext) {
    throw new Error(
      'useContractsContext must be used within a ContractsProvider',
    );
  }

  return contractsContext;
};

const ContractsContextProvider: React.FC = ({ children }) => {
  const { page: initialPage } = useParams<{ page?: string }>();
  const [text, setText] = React.useState('');
  const [page, setPage] = React.useState(parseInt(initialPage ?? '1', 10));
  const [total, setTotal] = React.useState(0);
  const [initialTotal, setInitialTotal] = React.useState(0);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [contracts, setContracts] = React.useState<ParsedOrgContractType[]>([]);
  const [searchParam, setSearchParam] = useSearchParams<{
    sort: TableSortType;
  }>();
  const [sort, setSort] = React.useState<TableSortType>({
    ...searchParam.sort,
  });

  const searchDeps: React.DependencyList = [sort, text];
  const { auth } = useContext(AuthContext);

  const fetchContracts = async () => {
    setLoading(true);

    if (!auth.org) {
      setLoading(false);
      return;
    }

    try {
      // trigger refresh of contracts for the org
      await refreshOwnerContractsByMLSStaffOrgId({
        mlsStaffOrgId: auth.org.id,
      });

      // fetch the contracts
      const contractsData = await getOrgContracts({
        mlsStaffOrgId: auth.org.id,
      });

      setContracts(contractsData);
      setTotal(contractsData.length);
      setInitialTotal(contractsData.length);
    } catch (err) {
      message.error(
        err.response?.data?.message || 'Something went wrong please try again',
      );
    }

    setLoading(false);
  };

  const debouncedFetchContracts = React.useCallback(
    debounce(fetchContracts, 500),
    [...searchDeps, page],
  );

  React.useEffect(() => {
    debouncedFetchContracts();
  }, [...searchDeps, page]);

  return (
    <ContractsContext.Provider
      value={{
        text,
        page,
        total,
        initialTotal,
        loading,
        contracts,
        sort,
        setSort,
        setInitialTotal,
        setText,
        setPage,
        setTotal,
        setContracts,
        setLoading,
      }}
    >
      {children}
    </ContractsContext.Provider>
  );
};

export default ContractsContextProvider;
